import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface HealthScore {
  id: string
  customer_name: string
  overall_score: number
  engagement_score: number
  product_usage_score: number
  support_score: number
  payment_score: number
  trend: string
}

interface HealthFactor {
  id: string
  name: string
  weight: number
  avg_score: number
  impact: string
}

interface HealthTrend {
  id: string
  period: string
  avg_health_score: number
  healthy_customers: number
  at_risk_customers: number
  churned_customers: number
}

interface Statistics {
  avg_health_score: number
  healthy_percentage: number
  at_risk_percentage: number
  improvement_rate: number
}

export default function CustomerHealthPage() {
  const [scores, setScores] = useState<HealthScore[]>([])
  const [factors, setFactors] = useState<HealthFactor[]>([])
  const [trends, setTrends] = useState<HealthTrend[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [scoresRes, factorsRes, trendsRes, statsRes] = await Promise.all([
        fetch('/api/customerhealth/scores'),
        fetch('/api/customerhealth/factors'),
        fetch('/api/customerhealth/trends'),
        fetch('/api/customerhealth/statistics')
      ])
      if (scoresRes.ok) setScores(await scoresRes.json())
      if (factorsRes.ok) setFactors(await factorsRes.json())
      if (trendsRes.ok) setTrends(await trendsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching customer health data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getHealthColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getHealthBg = (score: number) => {
    if (score >= 80) return 'bg-green-500'
    if (score >= 60) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  const getTrendIcon = (trend: string) => {
    if (trend === 'improving') return '↑'
    if (trend === 'declining') return '↓'
    return '→'
  }

  const getTrendColor = (trend: string) => {
    if (trend === 'improving') return 'text-green-600'
    if (trend === 'declining') return 'text-red-600'
    return 'text-gray-600'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Customer Health</h1>
          <p className="mt-2 text-gray-600">Phase 226 - Customer health scoring and monitoring</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Health Score</p>
              <p className={`text-3xl font-bold ${getHealthColor(statistics.avg_health_score)}`}>
                {statistics.avg_health_score}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Healthy Customers</p>
              <p className="text-2xl font-bold text-green-600">{statistics.healthy_percentage}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">At Risk</p>
              <p className="text-2xl font-bold text-red-600">{statistics.at_risk_percentage}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Improvement Rate</p>
              <p className="text-2xl font-bold text-blue-600">+{statistics.improvement_rate}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Customer Health Scores</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Overall</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engagement</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Usage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Support</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trend</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {scores.map((score) => (
                  <tr key={score.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{score.customer_name}</td>
                    <td className={`px-6 py-4 font-bold ${getHealthColor(score.overall_score)}`}>{score.overall_score}</td>
                    <td className={`px-6 py-4 ${getHealthColor(score.engagement_score)}`}>{score.engagement_score}</td>
                    <td className={`px-6 py-4 ${getHealthColor(score.product_usage_score)}`}>{score.product_usage_score}</td>
                    <td className={`px-6 py-4 ${getHealthColor(score.support_score)}`}>{score.support_score}</td>
                    <td className={`px-6 py-4 ${getHealthColor(score.payment_score)}`}>{score.payment_score}</td>
                    <td className={`px-6 py-4 ${getTrendColor(score.trend)}`}>{getTrendIcon(score.trend)} {score.trend}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Health Factors</h2>
            </div>
            <div className="p-6 space-y-4">
              {factors.map((factor) => (
                <div key={factor.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{factor.name}</span>
                    <span className={`text-sm ${factor.impact === 'high' ? 'text-red-600' : factor.impact === 'medium' ? 'text-yellow-600' : 'text-green-600'}`}>
                      {factor.impact} impact
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className={`h-2 rounded-full ${getHealthBg(factor.avg_score)}`} style={{ width: `${factor.avg_score}%` }} />
                    </div>
                    <span className="text-gray-500 text-sm">{factor.avg_score}%</span>
                  </div>
                  <p className="text-gray-500 text-sm">Weight: {factor.weight}%</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Health Trends</h2>
            </div>
            <div className="p-6 space-y-4">
              {trends.map((trend) => (
                <div key={trend.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{trend.period}</span>
                    <span className={`text-lg font-bold ${getHealthColor(trend.avg_health_score)}`}>
                      {trend.avg_health_score}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Healthy</p>
                      <p className="font-medium text-green-600">{trend.healthy_customers}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">At Risk</p>
                      <p className="font-medium text-yellow-600">{trend.at_risk_customers}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Churned</p>
                      <p className="font-medium text-red-600">{trend.churned_customers}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
